import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildItemListSchema, buildBreadcrumbSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What is the best help desk software for small business in 2026?',
    a: 'For small businesses in 2026, the best help desk software balances a usable free start, predictable growth costs, and included AI. Kora leads for small teams (pay-as-you-go credits ($1 = 10 credits, 1 credit per ticket, 1,100 free on signup), unlimited members, AI ticket structuring included). Freshdesk is the budget per-seat option ($19/agent/month on Growth). Zoho Desk fits teams already on Zoho (free for 3 users). Help Scout suits email-first teams. Zendesk remains the enterprise standard when budget is not the constraint.',
  },
  {
    q: 'What should small businesses look for in help desk software?',
    a: 'Four things decide it for small teams: pricing model (per-ticket vs per-seat — a 10-seat team pays 10× on per-seat tools), whether AI costs extra or is included, how fast setup is (afternoon vs weeks), and whether the free tier is genuinely usable or time-capped. Also check how the API is metered — Kora charges 1 credit per 400 API responses.',
  },
  {
    q: 'Is there good free help desk software?',
    a: 'Kora has no seat fees and unlimited members: new workspaces get 1,100 free credits with no card, then a ticket costs $0.10 (1 credit). AI ticket structuring is free. Freshdesk offers a 14-day trial (no free plan on its pricing page, checked 2026-10-19). Zoho Desk is free for 3 users. osTicket and FreeScout are free forever if you self-host.',
  },
];

const PICKS = [
  {
    rank: 1,
    name: 'Kora',
    bestFor: 'Small-to-mid teams that want AI included and no seat fees',
    pricing: 'Pay-as-you-go: $1 = 10 credits; 1,100 free credits on signup',
    strengths: ['AI ticket structuring included', 'No per-seat pricing', 'Public API included', 'Afternoon setup'],
    tradeoff: 'Newer product — smaller integration marketplace than Zendesk',
    path: '/',
  },
  {
    rank: 2,
    name: 'Freshdesk',
    bestFor: 'Budget-conscious teams comfortable with per-seat pricing',
    pricing: 'Growth $19/agent/mo, Pro $55/agent/mo (annual billing)',
    strengths: ['Mature feature set', 'Large marketplace', 'Familiar Freshworks ecosystem'],
    tradeoff: 'AI tied to higher tiers and add-ons; costs scale per agent',
    path: '/compare/kora-vs-freshdesk',
  },
  {
    rank: 3,
    name: 'Zoho Desk',
    bestFor: 'Teams already running on the Zoho ecosystem',
    pricing: 'Free for 3 users; paid from $7/agent/mo',
    strengths: ['Cheapest per-seat entry', 'Bundles with Zoho CRM/Books', 'Solid SLA features'],
    tradeoff: 'UI feels dated next to newer tools; AI (Zia) is an add-on',
    path: '/compare/kora-vs-zoho-desk',
  },
  {
    rank: 4,
    name: 'Help Scout',
    bestFor: 'Email-first support teams that value simplicity',
    pricing: 'Free plan: up to 5 users, 100 contacts; Standard $25/user/mo',
    strengths: ['Cleanest shared-inbox UX', 'Strong docs product', 'No ticket-portal feel for customers'],
    tradeoff: 'AI features gated to higher tiers; per-user pricing',
    path: '/compare/kora-vs-help-scout',
  },
  {
    rank: 5,
    name: 'Zendesk',
    bestFor: 'Enterprise teams with complex omnichannel needs',
    pricing: 'Suite Team $55/agent/mo; Professional $115/agent/mo',
    strengths: ['Deepest enterprise feature set', 'Ecosystem and integrations', 'Scales to hundreds of agents'],
    tradeoff: 'Highest cost at team sizes under ~25 agents; AI is a paid add-on; heavy setup',
    path: '/compare/kora-vs-zendesk',
  },
];

export function BestHelpdeskSoftwarePage() {
  useEffect(() => {
    const title = 'Best Helpdesk Software for Small Teams (2026) — Compared';
    const description =
      'The best helpdesk software for small teams in 2026, compared on pricing model, free tier, AI inclusion, and setup time — with honest tradeoffs for each.';
    setDocumentMeta(title, description);
    setCanonical('/best-helpdesk-software');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/best-helpdesk-software', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Best Helpdesk Software', url: 'https://usekora.app/best-helpdesk-software' },
      ]),
      buildItemListSchema(
        PICKS.map((p) => ({ name: p.name, url: `https://usekora.app${p.path}`, description: p.bestFor })),
      ),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Comparison · Updated September 2026"
      title="Best helpdesk software for small teams"
      subtitle="Five options compared on what actually matters at small-team sizes: pricing model, free tier, AI, and setup."
      answer={
        <p>
          The best helpdesk software for small teams in 2026: <strong>Kora</strong> is the pick
          for pricing without seat fees and with AI included (pay-as-you-go credits: $1 = 10 credits, 1 credit per ticket, 1,100 free on signup, unlimited members). <strong>Freshdesk</strong> is the budget per-seat option (Growth $19/agent/month).{' '}
          <strong>Zoho Desk</strong> suits teams on the Zoho ecosystem (free for 3 users).{' '}
          <strong>Help Scout</strong> fits email-first teams ($25/user/month).{' '}
          <strong>Zendesk</strong> remains the enterprise standard (Suite Team from
          $55/agent/month). The decisive factors at small scale are whether pricing is per-ticket or
          per-seat, whether AI costs extra, and how usable the free start actually is. Pricing
          checked in October 2026 and re-checked quarterly.
        </p>
      }
    >
      <ContentSection title="How we compared">
        <p>
          Four criteria matter most when the team is small: the pricing model (per-ticket versus
          per-seat — a 10-person team pays 10× on per-seat tools), AI inclusion (included,
          add-on, or metered), the honesty of the free start (member limits versus
          time limits), and setup time (an afternoon versus weeks of configuration). All pricing
          below was verified in June 2026 from official pricing pages.
        </p>
      </ContentSection>

      {PICKS.map((pick) => (
        <ContentSection key={pick.name} title={`${pick.rank}. ${pick.name}`}>
          <p className="font-medium text-zinc-700 ">
            Best for: {pick.bestFor}
          </p>
          <p className="text-xs text-zinc-400 ">{pick.pricing}</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-2 not-prose">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-green-600 mb-1.5">Strengths</div>
              <ul className="space-y-1">
                {pick.strengths.map((s) => (
                  <li key={s} className="text-sm text-zinc-600 ">• {s}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 mb-1.5">Tradeoffs</div>
              <p className="text-sm text-zinc-600 ">{pick.tradeoff}</p>
            </div>
          </div>
          {pick.path !== '/' && (
            <p className="mt-2">
              <Link to={pick.path} className="underline hover:text-zinc-900 ">
                Full Kora vs {pick.name} comparison →
              </Link>
            </p>
          )}
        </ContentSection>
      ))}

      <ContentSection title="Quick decision guide">
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">If you…</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Pick</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-zinc-100 "><td className="px-4 py-3 text-zinc-600 ">Want no seat fees and pay-per-ticket pricing</td><td className="px-4 py-3 font-medium">Kora</td></tr>
              <tr className="border-b border-zinc-100 "><td className="px-4 py-3 text-zinc-600 ">Already pay for Zoho products</td><td className="px-4 py-3 font-medium">Zoho Desk</td></tr>
              <tr className="border-b border-zinc-100 "><td className="px-4 py-3 text-zinc-600 ">Live in a shared Gmail inbox today</td><td className="px-4 py-3 font-medium">Help Scout</td></tr>
              <tr className="border-b border-zinc-100 "><td className="px-4 py-3 text-zinc-600 ">Need enterprise omnichannel at 50+ agents</td><td className="px-4 py-3 font-medium">Zendesk</td></tr>
              <tr className="border-b border-zinc-100 last:border-0"><td className="px-4 py-3 text-zinc-600 ">Want per-seat pricing with a big marketplace</td><td className="px-4 py-3 font-medium">Freshdesk</td></tr>
            </tbody>
          </table>
        </div>
      </ContentSection>

      <ContentSection title="Don't ship without a knowledge base">
        <p>
          Self-service is the cheapest support channel you have — 81% of customers check help articles
          before filing a ticket. Kora includes a branded, blog-style helpdesk with analytics and
          email subscriptions: 1 credit per published article, edits free.{' '}
          <Link to="/knowledge-base-software" className="underline hover:text-zinc-900 ">See the Helpdesk →</Link>
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions small teams ask when choosing help desk software." />
    </ContentPage>
  );
}
